import { createRouter, createWebHistory } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { useAuthStore } from './modules/auth/stores/auth-store'
import NotFoundPage from './modules/core/pages/not-found.vue'
import { companyUserRedirect } from './modules/companies/utils/company-user-redirect'
import { isChunkLoadFailure } from './modules/shared/utils/chunk-load-failure'
import AdminRoutes from './modules/admin/routes/admin-routes'
import AttachingRoutes from './modules/attaching/routes/attaching-routes'
import AuthRoutes from './modules/auth/routes/auth-routes'
import ApplicationsRoutes from './modules/applications/routes/applications-routes'
import BillingRoutes from './modules/billing/routes/billing-routes'
import CompaniesRoutes from './modules/companies/routes/companies-routes'
import ConnectingRoutes from './modules/connecting/routes/connecting-routes'
import InvestingRoutes from './modules/investing/routes/investing-routes'
import DashboardRoutes from './modules/dashboard/routes/dashboard-routes'
import InsightsRoutes from './modules/insights/routes/insights-routes'
import PublicRoutes from './modules/public/routes/public-routes'
import SandboxRoutes from './modules/sandbox/routes/sandbox-routes'
import SigningRoutes from './modules/signing/routes/signing-routes'
import TaxRoutes from './modules/tax/routes/tax-routes'
import FilingRoutes from './modules/filing/routes/filing-routes'
import MessagingRoutes from './modules/messaging/routes/messaging-routes'
import WorkspaceRoutes from './modules/workspace/routes/workspace-routes'
import { routes as fileRoutes } from 'vue-router/auto-routes'

const routes = [
  ...AdminRoutes,
  ...AttachingRoutes,
  ...AuthRoutes,
  ...ApplicationsRoutes,
  ...BillingRoutes,
  ...CompaniesRoutes,
  ...ConnectingRoutes,
  ...InvestingRoutes,
  ...PublicRoutes,
  ...SandboxRoutes,
  ...SigningRoutes,
  ...TaxRoutes,
  ...DashboardRoutes,
  ...InsightsRoutes,
  ...FilingRoutes,
  ...MessagingRoutes,
  ...WorkspaceRoutes,
  ...fileRoutes,

  // Generic 404 — Rails redirect controller sends users here when their
  // requested subdomain doesn't match a real Group. The auth guard's
  // allowlist (below) skips authentication checks for this route so it
  // never triggers the refresh/logout cycle.
  { path: '/not-found', name: 'not-found', component: NotFoundPage },

  // Any URL that doesn't match a registered route lands on the NotFound
  // page — covers typo'd paths (e.g. /:slug/dashboard2) and fully random
  // URLs alike.
  { path: '/:pathMatch(.*)*', redirect: { name: 'not-found' } },
]

const router = createRouter({
  history: createWebHistory(),
  // @ts-ignore
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    return {
      top: 0,
    }
  },
})

// handle auth flow where user arrives from the rails app with a temp token as a GET param
router.beforeEach(async (to, _from, next) => {
  if (to.query.t === undefined) return next()

  const authStore = useAuthStore()

  await authStore.refresh(typeof to.query.t === 'string' ? to.query.t : to.query.t.join(''), to)
  const query = { ...to.query }
  delete query.t
  next({ path: to.path, query: query })
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  if (
    [
      'login',
      'logout',
      'auth.verify',
      'auth.enable-two-factor',
      'request-password-reset',
      'request-email-verification',
      'email-verification-instructions',
      'verify-email',
      'public.welcome',
      'public.goodbye',
      'public.groups.apply',
      'public.investors.apply',
      'public.group-users.apply',
      'public.companies.apply',
      'reset-password',
      'reset-two-factor',
      'sandbox.sandbox',
      'spv-overview-v2',
      'not-found',
      // Root gateway routes itself via beforeEnter (Rails /user handoff or
      // /login) — refreshing here would call /v4/undefined/auth/current.
      'root',
    ].includes(to.name as string)
  ) {
    return next()
  }

  if (!authStore.is_authenticated) {
    await authStore.logout()
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // refresh() derives the API path from params.slug; a slug-less route
  // would call /v4/undefined/auth/current and 404 into /not-found.
  if (!to.params.slug) {
    return next()
  }

  // No new token is in play on a routine navigation — passing `undefined`
  // (rather than the stored access_token) is what lets refresh() apply its
  // 60s TTL skip for same-slug re-navigations. Every caller that passes an
  // explicit token (the ?t= swap below, verify/apply/account pages) forces a
  // real round-trip.
  const result = await authStore.refresh(undefined, to)

  if (!result.ok) {
    // Bad slug in the URL → send the user to the 404 page without ending
    // their session. Genuine auth failure (token expired, etc.) still ends
    // the session and bounces to login as before.
    if (result.reason === 'slug_not_found') {
      return next({ name: 'not-found' })
    }
    await authStore.logout()
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }

  // Group-access gate: refresh succeeded (slug exists, session valid) but
  // the user has no role for this group. 404 without ending the session —
  // they remain logged in for groups they actually belong to.
  if (to.params.slug && !authStore.current_user.role) {
    return next({ name: 'not-found' })
  }

  if (to.name === 'dashboard' && authStore.isInvestor) {
    if (to.params.slug === 'connections') {
      return next({ name: 'connecting.liquifi', params: { slug: to.params.slug } })
    }
    return next({
      name: 'investing.individual-overview',
      params: { slug: to.params.slug, individual_id: authStore.current_user.investor_id },
    })
  }

  // Company users have no dashboard of their own — the company profile is it
  // (tickets/2658). Without a company yet they belong in the onboarding wizard.
  if (to.name === 'dashboard' && authStore.is_company_user) {
    if (authStore.current_user.company_id) {
      return next({
        name: 'companies.profile',
        params: { slug: to.params.slug, companyId: String(authStore.current_user.company_id) },
      })
    }
    return next({ name: 'companies.onboarding', params: { slug: to.params.slug } })
  }

  const companyRedirect = companyUserRedirect(
    {
      name: to.name as string | null,
      companyId: typeof to.params.companyId === 'string' ? to.params.companyId : undefined,
      slug: String(to.params.slug),
    },
    authStore.current_user,
  )
  if (companyRedirect) return next(companyRedirect)

  next()
})

router.beforeEach((to, _from, next) => {
  // TODO fix typescript error
  // @ts-ignore
  document.title = to.meta?.title || 'Venture360'
  next()
})

router.afterEach((_to, _from) => {
  if (window.Intercom) {
    window.Intercom('update', {})
  }
})

// Route components load lazily, so a deploy that ships while users have the
// app open invalidates the hashed chunk URLs they still reference — the next
// navigation to an unvisited page fails with "Failed to fetch dynamically
// imported module" (wording varies by browser — see chunk-load-failure.ts;
// Safari's "Load failed" joined the matcher in tickets/3269). Recover by
// doing a full page load of the target route, which picks up the new deploy's
// chunk manifest. Reloads at most once per failed route; a successful
// navigation clears the retry marker so a later failure can still recover.
const CHUNK_RETRY_KEY = 'chunk-error-retried-route'

router.onError((error, to) => {
  if (!isChunkLoadFailure(error)) return
  if (!to?.fullPath || sessionStorage.getItem(CHUNK_RETRY_KEY) === to.fullPath) return

  // Observability for the recovery itself (tickets/3269): manual repro of the
  // stale-tab scenario is impossible, so the breadcrumb is how we verify the
  // fix works — V-APP-16C (Safari) rates should collapse and these should
  // appear. No-op when Sentry is not initialized (blank DSN).
  Sentry.addBreadcrumb({
    category: 'router',
    message: 'chunk-load failure — recovering via full reload',
    level: 'info',
    data: { route: to.fullPath, error: (error as Error | null)?.message ?? null },
  })

  sessionStorage.setItem(CHUNK_RETRY_KEY, to.fullPath)
  window.location.assign(to.fullPath)
})

router.afterEach((to) => {
  if (sessionStorage.getItem(CHUNK_RETRY_KEY) === to.fullPath) {
    sessionStorage.removeItem(CHUNK_RETRY_KEY)
  }
})

export default router
